/**
 * Rate guarantee and rate cap persist at plan level (siblings of `benefits`)
 * but are presented as a benefit-body field trio — `rateGuaranteeType`,
 * `rateGuaranteeAmount`, `rateGuaranteeCap` — so both plan-building surfaces
 * translate between the two shapes: flatten on read, lift on save.
 */
import { RfpBenefitPlan } from "../types/rfp.api.types";

type RateGuaranteeType = NonNullable<RfpBenefitPlan["rateGuarantee"]>["type"];

// Values are `unknown` because AG Grid's auto cell data type detection can hand
// back a type the cell spec never declared — a cleared text cell parses to 0,
// which the API rejects for the string `rateCap`.
type RateGuaranteeGridFields = {
  rateGuaranteeType?: unknown;
  rateGuaranteeAmount?: unknown;
  rateGuaranteeCap?: unknown;
};

/**
 * Merges the plan-level values into the benefit body as the flat keys the grid
 * cells read. Returns `benefits` untouched when the plan carries neither, so a
 * plan with no rate guarantee serializes exactly as it did before.
 */
export const flattenRateGuaranteeIntoBenefits = <TBenefits>(
  benefits: TBenefits,
  rateGuarantee: RfpBenefitPlan["rateGuarantee"],
  rateCap: RfpBenefitPlan["rateCap"],
): TBenefits => {
  if (!rateGuarantee && rateCap == null) {
    return benefits;
  }

  const base =
    benefits && typeof benefits === "object"
      ? (benefits as Record<string, unknown>)
      : {};

  return {
    ...base,
    rateGuaranteeType: rateGuarantee?.type,
    rateGuaranteeAmount: rateGuarantee?.amount,
    rateGuaranteeCap: rateCap,
  } as TBenefits;
};

/**
 * Reverses the flatten for the save path, matching the Quotes surface's
 * `syncRateGuaranteeToPlanLevel`. Every value is type-checked rather than
 * passed through: `rateGuarantee` needs a type (the API rejects one whose type
 * is null), `amount` must be numeric, and `rateCap` must be a string.
 */
export const liftRateGuaranteeFromBenefits = (
  benefits: unknown,
): Pick<RfpBenefitPlan, "rateGuarantee" | "rateCap"> => {
  const { rateGuaranteeType, rateGuaranteeAmount, rateGuaranteeCap } =
    (benefits as RateGuaranteeGridFields | null | undefined) ?? {};

  return {
    rateGuarantee:
      typeof rateGuaranteeType === "string" && rateGuaranteeType !== ""
        ? {
            type: rateGuaranteeType as RateGuaranteeType,
            ...(typeof rateGuaranteeAmount === "number"
              ? { amount: rateGuaranteeAmount }
              : {}),
          }
        : undefined,
    rateCap:
      typeof rateGuaranteeCap === "string" && rateGuaranteeCap !== ""
        ? rateGuaranteeCap
        : undefined,
  };
};
